import React, { useEffect, useState } from "react";
import { Box, Heading, Text, Button, Flex } from "@chakra-ui/react";
import { getPendingUsers, updateUser } from "../utils/api";

const getUserId = (user) =>
  user?.id ?? user?.user_id ?? user?.userId ?? user?.UserId ?? user?.uuid;

const AdminDashboard = () => {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await getPendingUsers();
        if (!res.ok) throw new Error("Failed to fetch users");

        const data = await res.json();
        console.log("Fetched Users:", data);
        setUsers(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error fetching users:", err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
  }, []);

  if (isLoading) return <Text>Loading...</Text>;
  if (error) return <Text>Error: {error}</Text>;

  return (
    <Flex justify="center" align="center">
      <Box w="80%">
        <div className="approval-container">
          {users.length > 0 ? (
            users.map((user) => {
              const uid = getUserId(user);
              return (
                <UserCard
                  key={uid || `${user.email}-${user.full_name}`} // safe fallback
                  user={user}
                  userId={uid}
                  setUsers={setUsers}
                />
              );
            })
          ) : (
            <Text>No pending users found.</Text>
          )}
        </div>
      </Box>
    </Flex>
  );
};

const UserCard = ({ user, userId, setUsers }) => {
  const [showError, setShowError] = useState(false);

  const handleApprove = async () => {
    setShowError(false);

    // ✅ If userId is missing, don’t even call the API
    if (!userId) {
      console.error("No usable user id found on user object:", user);
      setShowError(true);
      return;
    }

    try {
      console.log(`Approving user with ID: ${userId}`);

      const response = await updateUser(
        { approved: true, isDeleted: false },
        userId
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        console.error("Approval failed:", data?.message || data);
        setShowError(true);
        return;
      }

      console.log("Approve API Response:", data);

      // ✅ Remove user from UI on success
      setUsers((prevUsers) =>
        prevUsers.filter((u) => (u.id ?? u.user_id ?? u.userId ?? u.UserId ?? u.uuid) !== userId)
      );
    } catch (error) {
      console.error("Approve error:", error);
      setShowError(true);
    }
  };

  const handleDelete = async () => {
    setShowError(false);

    if (!userId) {
      console.error("No usable user id found on user object:", user);
      setShowError(true);
      return;
    }

    try {
      console.log(`Deleting user with ID: ${userId}`);

      const response = await updateUser({ isDeleted: true }, userId);
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        console.error("Delete failed:", data?.message || data);
        setShowError(true);
        return;
      }

      setUsers((prevUsers) =>
        prevUsers.filter((u) => (u.id ?? u.user_id ?? u.userId ?? u.UserId ?? u.uuid) !== userId)
      );
    } catch (error) {
      console.error("Delete error:", error);
      setShowError(true);
    }
  };

  return (
    <div className="admin-card-container-approval">
      <Box
        className="admin-card-approval"
        m={15}
        p={8}
        rounded="lg"
        boxShadow="0px 10px 20px rgba(0, 0, 0, 0.1), 0px 6px 6px rgba(0, 0, 0, 0.1)"
        w={{ base: "90%", sm: "400px" }}
        display="flex"
        flexDirection="column"
        gap={4}
      >
        <Box>
          <Heading as="h3" size="md">
            Name:&nbsp;&nbsp;{user.full_name}
          </Heading>
          <Text className="approve-text">Email:&nbsp;&nbsp;{user.email}</Text>
          <Text className="approve-text">Role:&nbsp;&nbsp;{user.role}</Text>

          {/* Helpful debug (remove later) */}
          <Text fontSize="sm" color="gray.500">
            User ID: {String(userId || "missing")}
          </Text>

          {showError && (
            <Text color="red.500">Something went wrong, please try again.</Text>
          )}
        </Box>

        <Flex justify="flex-end" gap={3} className="button-container">
          <Button colorScheme="red" onClick={handleDelete}>
            Delete
          </Button>
          <Button bg="#00aeef" color="#ffffff" onClick={handleApprove}>
            Approve
          </Button>
        </Flex>
      </Box>
    </div>
  );
};

export default AdminDashboard;
